import { format, isValid } from "date-fns";

import { DATE_FORMAT, MS_PER_DAY } from "./constants";

/** Type guard: is `value` a bare `yyyy-MM-dd` date-only string? */
export const isDateOnlyString = (value: unknown): value is string => {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
};

/**
 * Formats a `Date`, defaulting to the standard application ISO date format
 * (`yyyy-MM-dd`). Pass `dateFormat` to override.
 *
 * Name note: this `formatDate` takes a `Date`. The org-aware `formatDate` on the
 * `@ethisyscore/core-utils/date/org-format` sub-path takes an ISO STRING and
 * returns org-configured display text — a different contract that never collides
 * because it lives on a different entry point. For the common Date→`yyyy-MM-dd`
 * case prefer {@link toIsoDate}, whose name sidesteps the overload.
 */
export const formatDate = (date: Date, dateFormat: string = DATE_FORMAT): string => {
  return format(date, dateFormat);
};

/** Today's date as a `yyyy-MM-dd` ISO string (local calendar day). */
export const getTodayIsoDate = (): string => {
  return formatDate(new Date());
};

/**
 * Formats a `Date` as a `yyyy-MM-dd` ISO date-only string in local time — the
 * inverse of {@link parseIsoDateLocal}. Equivalent to `formatDate(date)`, exposed
 * under the clearer name for the date-only round-trip.
 */
export const toIsoDate = (date: Date): string => formatDate(date);

/**
 * Returns a new local `Date` at the first day of `date`'s month, offset by `months`
 * (may be negative), normalising the day-of-month to 1 — e.g. offsetting 15 Mar 2026
 * by -1 gives 1 Feb 2026, not 15 Feb. Returns an `Invalid Date` for a null/invalid
 * input so month-axis arithmetic propagates NaN rather than throwing. Used for
 * month-granular axis/range building (e.g. a Gantt chart).
 */
export const startOfMonthOffset = (date: Date | null | undefined, months: number): Date => {
  if (!date || !isValid(date)) {
    return new Date(NaN);
  }
  return new Date(date.getFullYear(), date.getMonth() + months, 1);
};

/**
 * Whole-day difference between two `yyyy-MM-dd` date-only strings (`toIso - fromIso`),
 * rounded to guard against DST half-day drift when the range crosses a clock-change
 * boundary. Parses both bounds as LOCAL dates (via {@link parseIsoDateLocal}); returns
 * `NaN` when either is not a valid date-only string.
 */
export const daysBetweenIso = (fromIso: string, toIso: string): number => {
  const from = parseIsoDateLocal(fromIso);
  const to = parseIsoDateLocal(toIso);
  if (!from || !to) return NaN;
  return Math.round((to.getTime() - from.getTime()) / MS_PER_DAY);
};

/** First day of the given date's month, as a `yyyy-MM-dd` ISO string. */
export const getMonthStartIso = (date: Date): string =>
  formatDate(new Date(date.getFullYear(), date.getMonth(), 1));

/** Last day of the given date's month, as a `yyyy-MM-dd` ISO string. */
export const getMonthEndIso = (date: Date): string =>
  formatDate(new Date(date.getFullYear(), date.getMonth() + 1, 0));

/**
 * Parses a date-only `yyyy-MM-dd` string as a LOCAL date. Returns `null` when
 * the value is not a date-only string or is not a valid calendar date. Avoids
 * the `new Date("yyyy-MM-dd")` UTC parse, which shifts the day in non-UTC zones.
 */
export const parseIsoDateLocal = (value: string | null | undefined): Date | null => {
  if (!value || !isDateOnlyString(value)) return null;
  const [year, month, day] = value.split("-").map(Number);
  const d = new Date(year, month - 1, day);
  return isValid(d) ? d : null;
};

/**
 * Adds `days` to an ISO `yyyy-MM-dd` date and returns the result as a `yyyy-MM-dd`
 * string. Parses and formats in local time (via `parseIsoDateLocal` / `formatDate`)
 * so the arithmetic can't drift across a day boundary in non-UTC timezones/DST.
 * Returns the input unchanged when it is not a valid date-only string.
 */
export const addDaysToIsoDate = (isoDate: string, days: number): string => {
  const base = parseIsoDateLocal(isoDate);
  if (!base) return isoDate;
  base.setDate(base.getDate() + days);
  return formatDate(base);
};

/**
 * Adds `years` to an ISO `yyyy-MM-dd` date and returns the result as a `yyyy-MM-dd`
 * string. Parses and formats in local time (like {@link addDaysToIsoDate}) so the
 * arithmetic can't drift across a day boundary in non-UTC timezones/DST. Used to seed
 * default expiry / end-of-term dates (insurance cover, licences, fixed-term contracts).
 * A 29 Feb base in a non-leap target year rolls to 1 Mar, matching `Date.setFullYear`.
 * Returns the input unchanged when it is not a valid date-only string.
 */
export const addYearsToIsoDate = (isoDate: string, years: number): string => {
  const base = parseIsoDateLocal(isoDate);
  if (!base) return isoDate;
  base.setFullYear(base.getFullYear() + years);
  return formatDate(base);
};

/**
 * Extracts a date-only string (`yyyy-MM-dd`) from a date string.
 * - Date-only strings (`2024-05-15`) are returned as-is.
 * - ISO datetime strings (`2024-05-15T00:00:00+05:30`) have the date portion
 *   extracted directly from the string to preserve the original calendar date
 *   without timezone conversion.
 * - Returns empty string for null, undefined, or unparseable values.
 */
export const toDateOnlyString = (value: string | null | undefined): string => {
  if (!value) return "";
  // Extract the yyyy-MM-dd portion before the "T" separator to avoid timezone
  // conversion that can shift the date by a day.
  const datepart = value.split("T")[0];
  return isDateOnlyString(datepart) ? datepart : "";
};
